import React from 'react';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import { FormModal } from '../../../common/components/FormModal';
import icons from '../../../resources/assets/icons';
import { IMonitorsState, IMonitorsStateErrors } from '../../../common/store/monitors/MonitorsInterfaces';
import { getMonitorsError } from '../../../common/store/monitors/MonitorsSelectors';
import { IDeleteMonitorPayload } from '../../../common/payloads/monitors/IDeleteMonitorPayload';
import { deleteMonitor } from '../../../common/store/monitor/MonitorActions';
import { IUiState } from '../../../common/store/ui/IUiInterfaces';
import { toggleErrorModal, toggleSuccessModal } from '../../../common/store/ui/UiActions';
import './GeneralErrorModal.css';

const mapStateToProps = (
  { ui }: { ui: IUiState },
) => ({
  ui,
  globalSuccess: ui.globalSuccess,
  globalError: ui.globalError,
});

const mapDispatchToProps = (dispatch: any) => bindActionCreators(
  {
    toggleErrorModal,
    toggleSuccessModal
  },
  dispatch,
);

interface IGeneralErrorModalProps {
  toggleErrorModal: Function;
  toggleSuccessModal: Function;
  ui: IUiState;
}

interface IGeneralErrorModalState {
  showDetails: boolean;
  copySuccess: boolean;
  timeLeft: number;
}

class GeneralErrorModal extends React.Component<IGeneralErrorModalProps, IGeneralErrorModalState> {

  static defaultProps = {
  };

  private autoCloseTimer: NodeJS.Timeout | null = null;

  constructor(props: IGeneralErrorModalProps) {
    super(props);
    this.state = {
      showDetails: false,
      copySuccess: false,
      timeLeft: 0,
    };
  }

  componentDidMount() {
    this.setupAutoClose();
  }

  componentDidUpdate(prevProps: IGeneralErrorModalProps) {
    if (prevProps.ui.globalError.show !== this.props.ui.globalError.show) {
      if (this.props.ui.globalError.show) {
        this.setupAutoClose();
      } else {
        this.clearAutoClose();
      }
    }
  }

  componentWillUnmount() {
    this.clearAutoClose();
  }

setupAutoClose = () => {
  this.clearAutoClose();
  const { autoClose } = this.props.ui.globalError;
  if (autoClose && autoClose > 0) {
    this.setState({ timeLeft: autoClose }, () => {
      this.autoCloseTimer = setInterval(() => {
        this.setState(prevState => {
          const newTimeLeft = prevState.timeLeft - 1;
          if (newTimeLeft <= 0) {
            this.handleClose();
            return { timeLeft: 0 };
          }
          return { timeLeft: newTimeLeft };
        });
      }, 1000);
    });
  }
}

  clearAutoClose = () => {
    if (this.autoCloseTimer) {
      clearInterval(this.autoCloseTimer);
      this.autoCloseTimer = null;
    }
  }

  handleClose = () => {
    this.clearAutoClose();
    this.setState({ showDetails: false, copySuccess: false, timeLeft: 0 });
    this.props.toggleErrorModal();
  }

  toggleDetails = () => {
    this.setState(prevState => ({ showDetails: !prevState.showDetails }));
  }

  copyToClipboard = async () => {
    const { globalError } = this.props.ui;
    const errorText = `Error: ${globalError.title}\nMensaje: ${globalError.message}\n${globalError.source ? `Fuente: ${globalError.source}\n` : ''}${globalError.details ? `Detalles: ${globalError.details}` : ''}`;
    
    try {
      await navigator.clipboard.writeText(errorText);
      this.setState({ copySuccess: true });
      setTimeout(() => this.setState({ copySuccess: false }), 2000);
    } catch (err) {
      console.error('Error al copiar al portapapeles:', err);
    }
  }

  getErrorTypeConfig = () => {
    console.log('Global Error:', this.props.ui.globalError);
    if (!this.props.ui.globalError || !this.props.ui.globalError.type) {
      return {
        color: '#dc3545',
        bgColor: '#dc3545',
        icon: icons.Alert,
        title: 'Error del Sistema'
      };
    }

    const { type = 'error' } = this.props.ui.globalError;
    
    const configs = {
      error: {
        color: '#dc3545',
        bgColor: '#dc3545',
        icon: icons.Alert,
        title: 'Error del Sistema'
      },
      warning: {
        color: '#ffc107',
        bgColor: '#ffc107',
        icon: icons.Alert,
        title: 'Advertencia'
      },
      info: {
        color: '#17a2b8',
        bgColor: '#17a2b8',
        icon: icons.info,
        title: 'Información'
      }
    };

    return configs[type];
  }

  getErrorIcon = () => {
    const config = this.getErrorTypeConfig();
    const IconComponent = config.icon;
    
    return (
      <div className="error-icon-container d-flex justify-content-center align-items-center mb-3">
        <div 
          className="error-icon-circle d-flex justify-content-center align-items-center rounded-circle" 
          style={{ 
            width: '60px', 
            height: '60px', 
            backgroundColor: config.bgColor,
            boxShadow: `0 4px 12px ${config.color}40`
          }}
        >
          <IconComponent 
            width="30px" 
            height="30px"
            fill="white"
          />
        </div>
      </div>
    );
  }

  formatErrorMessage = (message: string) => {
    if (message && message.includes('\n')) {
      return message.split('\n').map((line, index) => (
        <p key={index} className="mb-2 text-muted">
          {line}
        </p>
      ));
    }
    return <p className="text-muted">{message}</p>;
  }

  renderAutoCloseTimer = () => {
    const { timeLeft } = this.state;
    const { autoClose } = this.props.ui.globalError;
    
    if (autoClose && timeLeft > 0) {
      return (
        <div className="auto-close-timer text-center mt-3">
          <small className="text-muted">
            Este modal se cerrará automáticamente en {timeLeft} segundo{timeLeft !== 1 ? 's' : ''}
          </small>
          <div className="progress mt-2" style={{ height: '4px' }}>
            <div 
              className="progress-bar bg-secondary" 
              style={{ 
                width: `${(timeLeft / autoClose) * 100}%`,
                transition: 'width 1s linear'
              }}
            ></div>
          </div>
        </div>
      );
    }
    return null;
  }

  renderCopyButton = () => {
    const { canCopy } = this.props.ui.globalError;
    const { copySuccess } = this.state;
    
    if (!canCopy) return null;
    
    return (
      <button
        className="btn btn-outline-secondary btn-sm me-2"
        onClick={this.copyToClipboard}
        title="Copiar error al portapapeles"
      >
        {copySuccess ? (
          <>
            <icons.Alert width="14px" height="14px" className="me-1" />
            ¡Copiado!
          </>
        ) : (
          <>
            <icons.Alert width="14px" height="14px" className="me-1" />
            Copiar
          </>
        )}
      </button>
    );
  }

  renderDetailsButton = () => {
    const { details } = this.props.ui.globalError;
    const { showDetails } = this.state;
    
    if (!details) return null;
    
    return (
      <button
        className="btn btn-outline-secondary btn-sm"
        onClick={this.toggleDetails}
        title="Ver detalles técnicos"
      >
        <icons.Alert width="14px" height="14px" className="me-1" />
        {showDetails ? 'Ocultar detalles' : 'Ver detalles'}
      </button>
    );
  }

  renderErrorDetails = () => {
    const { globalError } = this.props.ui;
    const { showDetails } = this.state;
    const config = this.getErrorTypeConfig();
    
    return (
      <div className="error-details">
        {this.getErrorIcon()}
        
        <div className="text-center">
          <h4 className="error-title mb-3" style={{ color: config.color }}>
            {globalError.title || config.title}
          </h4>
          
          <div className="error-message mb-4">
            {this.formatErrorMessage(globalError.message)}
          </div>

          {globalError.source && (
            <div className="error-source mb-3">
              <small className="text-muted">
                <strong>Fuente:</strong> {globalError.source}
              </small>
            </div>
          )}

          {showDetails && globalError.details && (
            <div className="error-technical-details mt-3 p-3 bg-light rounded">
              <h6 className="text-muted mb-2">Detalles técnicos:</h6>
              <pre className="text-left small text-muted mb-0" style={{ whiteSpace: 'pre-wrap' }}>
                {globalError.details}
              </pre>
            </div>
          )}

          {(globalError.canCopy || globalError.details) && (
            <div className="error-actions mt-3 d-flex justify-content-center">
              {this.renderCopyButton()}
              {this.renderDetailsButton()}
            </div>
          )}

          {this.renderAutoCloseTimer()}
        </div>
      </div>
    );
  }
    
  render() {
    const config = this.getErrorTypeConfig();
    
    return (
      <FormModal
        isOpen={this.props.ui.globalError.show}
        onClose={this.handleClose}
        onSubmit={this.handleClose}
        submitButtonLabel={'Entendido'}
        footerClassName="modal-footer d-flex justify-content-center"
        hideCloseButton
        submitClassName={`px-4 ${this.props.ui.globalError.type === 'error' ? 'btn-danger' : this.props.ui.globalError.type === 'warning' ? 'btn-warning' : 'btn-info'}`}
        modalBodyClassName="p-4"
        contentClassName="border-0 shadow-lg"
        header={
          <div className="d-flex align-items-center" style={{ color: config.color }}>
            <config.icon width="20px" height="20px" fill="currentColor" className="me-2" />
            <span>{config.title}</span>
          </div>
        }
      >
        <div className="error-modal-content">
          {this.renderErrorDetails()}
        </div>
      </FormModal>
    );
  }
}

export default connect(mapStateToProps, mapDispatchToProps)(GeneralErrorModal);
